/* SUNSHINE+CLOUD — Catalog reader. No external libraries or tracking. */
(() => {
  'use strict';
  if (customElements.get('sc-catalog-reader')) return;

  class CatalogReader extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected && !this.controller) this.initialize();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.preloaded = null;
    }

    initialize() {
      this.pages = [...this.querySelectorAll('[data-page-id]')];
      if (!this.pages.length) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.isEditor = this.dataset.editor === 'true';
      this.issue = this.dataset.issue || '64';
      this.key = `sc-catalog:${location.pathname}:${this.issue}${this.isEditor ? `:${this.id}:editor` : ''}`;
      this.hashPrefix = `#sc-catalog-${encodeURIComponent(this.issue)}-`;
      this.controls = this.querySelector('[data-controls]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.picker = this.querySelector('[data-picker]');
      this.readButton = this.querySelector('[data-read]');
      this.zoomButton = this.querySelector('[data-zoom-open]');
      this.shop = this.querySelector('[data-shop]');
      this.productList = this.querySelector('[data-product-list]');
      this.status = this.querySelector('[data-status]');
      this.dialog = this.querySelector('dialog');
      this.zoomImage = this.querySelector('.sc-catalog__zoom-image');
      this.zoomScroll = this.querySelector('.sc-catalog__zoom-scroll');
      this.zoomCanvas = this.querySelector('.sc-catalog__zoom-canvas');
      this.zoomError = this.querySelector('.sc-catalog__zoom-error');
      this.scale = 1;
      this.pointers = new Set();
      const stored = this.load();
      this.storyOpen = stored.story === true;
      const hashIndex = this.indexFromHash();
      const storedIndex = this.pages.findIndex(page => page.dataset.pageId === stored.page);
      this.index = hashIndex >= 0 ? hashIndex : Math.max(0, storedIndex);

      this.on(this.prev, 'click', () => this.show(this.index - 1, true));
      this.on(this.next, 'click', () => this.show(this.index + 1, true));
      this.on(this.picker, 'change', () => this.show(Number(this.picker.value), true));
      this.on(this.readButton, 'click', () => this.setStory(!this.storyOpen, true));
      this.on(this.zoomButton, 'click', () => this.openZoom());
      this.on(this, 'click', event => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('[data-story-close]')) this.setStory(false, true);
        if (target?.closest('[data-retry]')) this.loadImage(this.pages[this.index], true);
        if (target?.closest('a[data-shop-link]')) this.remember(true);
      });
      this.on(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (this.dialog.open || event.altKey || event.metaKey || event.ctrlKey) return;
        if (event.target.closest('select, input, textarea, .sc-catalog__story')) return;
        if (!event.target.closest('[data-controls]')) return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      });

      for (const page of this.pages) {
        const img = page.querySelector('.sc-catalog__image');
        if (!img) continue;
        this.on(img, 'error', () => this.imageError(page, true));
        this.on(img, 'load', () => this.imageError(page, false));
      }
      this.setupSwipe();
      this.setupZoom();
      this.on(window, 'hashchange', () => {
        const index = this.indexFromHash();
        if (index >= 0) this.show(index, true);
      });
      this.on(window, 'pageshow', event => {
        if (event.persisted) this.restoreReturnPosition();
      });
      if (this.isEditor) {
        this.on(document, 'shopify:block:select', event => {
          if (!this.contains(event.target)) return;
          const index = this.pages.findIndex(page => page.dataset.pageId === event.detail?.blockId);
          if (index < 0) return;
          this.show(index, true);
          this.pages[index].scrollIntoView({ block: 'nearest' });
        });
      }

      this.show(this.index);
      this.controls.hidden = false;
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation?.type === 'back_forward') this.restoreReturnPosition();
    }

    on(target, event, callback, options = {}) {
      target.addEventListener(event, callback, { ...options, signal: this.signal });
    }

    indexFromHash() {
      return this.pages.findIndex(page => `${this.hashPrefix}${encodeURIComponent(page.dataset.pageId)}` === location.hash);
    }

    load() {
      try {
        const data = JSON.parse(sessionStorage.getItem(this.key) || '{}');
        return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
      } catch (_) {
        return {};
      }
    }

    remember(leaving = false) {
      const page = this.pages[this.index];
      if (!page) return;
      const data = { page: page.dataset.pageId, story: this.storyOpen, returning: leaving, scrollY: window.scrollY, time: Date.now() };
      try { sessionStorage.setItem(this.key, JSON.stringify(data)); } catch (_) { /* Storage may be disabled. */ }
      if (!this.isEditor && (!location.hash || location.hash.startsWith(this.hashPrefix))) {
        try {
          history.replaceState(history.state, '', `${location.pathname}${location.search}${this.hashPrefix}${encodeURIComponent(data.page)}`);
        } catch (_) { /* Reading still works without history access. */ }
      }
    }

    restoreReturnPosition() {
      if (this.isEditor) return;
      const saved = this.load();
      if (!saved.returning || !Number.isFinite(saved.scrollY) || Date.now() - saved.time > 86400000) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = requestAnimationFrame(() => {
          if (this.isConnected) window.scrollTo(0, saved.scrollY);
          this.remember();
        });
      });
    }

    show(index, announce = false) {
      if (!Number.isInteger(index) || index < 0 || index >= this.pages.length) return;
      this.index = index;
      for (const [i, page] of this.pages.entries()) {
        page.hidden = i !== index;
        if (i !== index) {
          const image = page.querySelector('.sc-catalog__image[src]');
          if (image) {
            image.dataset.src = image.getAttribute('src');
            if (image.hasAttribute('srcset')) image.dataset.srcset = image.getAttribute('srcset');
            image.removeAttribute('srcset');
            image.removeAttribute('src');
          }
        }
      }
      const page = this.pages[index];
      this.picker.value = String(index);
      this.prev.disabled = index === 0;
      this.next.disabled = index === this.pages.length - 1;
      this.loadImage(page);
      this.updateStory();
      this.zoomButton.disabled = !page.querySelector('[data-full-src]');
      const template = page.querySelector('template[data-products]');
      this.productList.replaceChildren(template.content.cloneNode(true));
      this.shop.hidden = !this.productList.querySelector('.sc-catalog__product, .sc-catalog__note');
      if (announce) {
        this.status.textContent = `CATALOG ${this.issue}、${page.dataset.pageLabel}。全${this.pages.length}面中${index + 1}面目。`;
        this.remember();
      }
      this.preloadNext();
    }

    loadImage(page, retry = false) {
      const img = page.querySelector('.sc-catalog__image');
      if (!img) return;
      if (retry) {
        this.imageError(page, false);
        const source = img.dataset.src || img.getAttribute('src');
        const sourceSet = img.dataset.srcset || img.getAttribute('srcset');
        img.removeAttribute('srcset');
        img.removeAttribute('src');
        if (sourceSet) img.setAttribute('srcset', sourceSet);
        if (source) img.setAttribute('src', source);
      } else if (img.dataset.src) {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.src;
        delete img.dataset.srcset;
      }
      if (img.complete && img.getAttribute('src')) this.imageError(page, img.naturalWidth === 0);
    }

    imageError(page, failed) {
      const notice = page.querySelector('[data-image-error]');
      if (notice) notice.hidden = !failed;
    }

    preloadNext() {
      if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const img = this.pages[this.index + 1]?.querySelector('.sc-catalog__image[data-src]');
      if (!img) return;
      const preload = new Image();
      preload.sizes = img.sizes;
      if (img.dataset.srcset) preload.srcset = img.dataset.srcset;
      preload.src = img.dataset.src;
      this.preloaded = preload;
    }

    updateStory() {
      const page = this.pages[this.index];
      for (const other of this.pages) {
        const story = other.querySelector('.sc-catalog__story');
        if (story) story.hidden = other !== page || !this.storyOpen;
      }
      const story = page.querySelector('.sc-catalog__story');
      this.readButton.hidden = !story;
      this.readButton.setAttribute('aria-expanded', String(!!story && this.storyOpen));
      this.readButton.textContent = this.storyOpen ? '紙面に戻る' : '文章を読む';
      if (story) this.readButton.setAttribute('aria-controls', story.id);
      else this.readButton.removeAttribute('aria-controls');
    }

    setStory(open, focus = false) {
      this.storyOpen = open;
      this.updateStory();
      this.remember();
      if (focus) {
        const target = open ? this.pages[this.index].querySelector('.sc-catalog__story') : this.readButton;
        target?.focus({ preventScroll: true });
      }
    }

    setupSwipe() {
      const papers = this.querySelector('.sc-catalog__papers');
      this.on(papers, 'pointerdown', event => {
        if (event.button !== 0 || event.target.closest('.sc-catalog__story, button, a')) return;
        this.pointers.add(event.pointerId);
        if (this.pointers.size > 1) { this.swipe = null; return; }
        this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() };
        papers.setPointerCapture(event.pointerId);
      });
      const finish = (event, cancelled) => {
        this.pointers.delete(event.pointerId);
        const start = this.swipe;
        if (!start || start.id !== event.pointerId) return;
        this.swipe = null;
        if (cancelled || this.pointers.size) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.6 && performance.now() - start.time < 1000) {
          this.show(this.index + (dx < 0 ? 1 : -1), true);
        }
      };
      this.on(window, 'pointerup', event => finish(event, false));
      this.on(window, 'pointercancel', event => finish(event, true));
      this.on(papers, 'lostpointercapture', event => { this.pointers.delete(event.pointerId); });
    }

    setupZoom() {
      this.on(this.querySelector('[data-zoom-close]'), 'click', () => this.dialog.close());
      this.on(this.dialog, 'close', () => {
        this.zoomButton.focus({ preventScroll: true });
        this.pan = null;
      });
      this.on(this.querySelector('[data-zoom-in]'), 'click', () => this.setZoom(Math.min(4, this.scale + .5)));
      this.on(this.querySelector('[data-zoom-out]'), 'click', () => this.setZoom(Math.max(1, this.scale - .5)));
      this.on(this.querySelector('[data-zoom-fit]'), 'click', () => this.setZoom(1));
      this.on(this.zoomImage, 'error', () => { this.zoomError.hidden = false; });
      this.on(this.zoomImage, 'load', () => { this.zoomError.hidden = true; });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.dialog.open) this.setZoom(this.scale);
        });
        this.resizeObserver.observe(this.zoomScroll);
      } else {
        this.on(window, 'resize', () => { if (this.dialog.open) this.setZoom(this.scale); });
      }
      this.on(this.zoomScroll, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || this.scale === 1) return;
        this.pan = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.zoomScroll.scrollLeft, top: this.zoomScroll.scrollTop };
        this.zoomScroll.setPointerCapture(event.pointerId);
        this.zoomScroll.dataset.dragging = 'true';
        event.preventDefault();
      });
      this.on(this.zoomScroll, 'pointermove', event => {
        if (!this.pan || this.pan.id !== event.pointerId) return;
        this.zoomScroll.scrollLeft = this.pan.left - (event.clientX - this.pan.x);
        this.zoomScroll.scrollTop = this.pan.top - (event.clientY - this.pan.y);
      });
      for (const eventName of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        this.on(this.zoomScroll, eventName, () => {
          this.pan = null;
          this.zoomScroll.dataset.dragging = 'false';
        });
      }
    }

    openZoom() {
      const image = this.pages[this.index].querySelector('[data-full-src]');
      if (!image) return;
      if (typeof this.dialog.showModal !== 'function') {
        window.open(image.dataset.fullSrc, '_blank', 'noopener');
        return;
      }
      this.zoomError.hidden = true;
      this.zoomImage.alt = image.alt;
      this.zoomImage.width = Number(image.getAttribute('width'));
      this.zoomImage.height = Number(image.getAttribute('height'));
      this.zoomAspect = this.zoomImage.width / this.zoomImage.height;
      this.zoomImage.src = image.dataset.fullSrc;
      this.querySelector('[data-zoom-title]').textContent = `CATALOG ${this.issue} / ${this.pages[this.index].dataset.pageLabel}`;
      this.dialog.showModal();
      this.scale = 1;
      this.frame = requestAnimationFrame(() => this.setZoom(1));
    }

    setZoom(scale) {
      if (!this.dialog.open) return;
      const viewport = this.zoomScroll;
      const width = viewport.clientWidth;
      const height = viewport.clientHeight;
      if (!width || !height) return;
      const ratio = this.zoomAspect || (1561 / 2270);
      const fit = Math.min(width, height * ratio);
      const previousWidth = this.zoomCanvas.offsetWidth || width;
      const previousHeight = this.zoomCanvas.offsetHeight || height;
      const centerX = (viewport.scrollLeft + width / 2) / previousWidth;
      const centerY = (viewport.scrollTop + height / 2) / previousHeight;
      this.scale = Math.max(1, Math.min(4, scale));
      const imageWidth = fit * this.scale;
      const canvasWidth = Math.max(width, imageWidth);
      const canvasHeight = Math.max(height, imageWidth / ratio);
      this.zoomImage.style.width = `${imageWidth}px`;
      this.zoomCanvas.style.width = `${canvasWidth}px`;
      this.zoomCanvas.style.height = `${canvasHeight}px`;
      viewport.scrollLeft = this.scale === 1 ? 0 : centerX * canvasWidth - width / 2;
      viewport.scrollTop = this.scale === 1 ? 0 : centerY * canvasHeight - height / 2;
      viewport.dataset.pannable = String(this.scale > 1);
      this.querySelector('[data-zoom-value]').value = `${Math.round(this.scale * 100)}%`;
      this.querySelector('[data-zoom-out]').disabled = this.scale === 1;
      this.querySelector('[data-zoom-in]').disabled = this.scale === 4;
    }
  }

  customElements.define('sc-catalog-reader', CatalogReader);
})();
